<template>
    <div class="absolute top-0 right-0">
        <div
            ref="referenceRef"
            class="px-1"
            tabindex="-1"
            @mouseover="show"
            @mouseleave="hide"
        >
            <slot name="tooltipAnchor"/>
        </div>
        <teleport v-if="needsTeleporting" to="body">
            <div
                ref="floatingRef"
                class="floating-ref !absolute"
                :class="[isHidden ? 'hidden' : '', errorTooltip ? 'error-tooltip' : 'normal-tooltip']"
            >
                <slot name="tooltipContent"/>
                <div
                    ref="arrowRef"
                    class="arrow-ref"
                    :class="[isHidden ? 'hidden' : '', errorTooltip ? 'error-tooltip' : 'normal-tooltip']"
                />
            </div>
        </teleport>
        <div
            v-else
            ref="floatingRef"
            class="floating-ref absolute"
            :class="[isHidden ? 'hidden' : '', errorTooltip ? 'error-tooltip' : 'normal-tooltip']"
        >
            <slot name="tooltipContent"/>
            <div
                ref="arrowRef"
                class="arrow-ref"
                :class="[isHidden ? 'hidden' : '', errorTooltip ? 'error-tooltip' : 'normal-tooltip']"
            />
        </div>
    </div>
    <slot name="content" />
</template>

<script setup lang="ts">

import {arrow, computePosition, ComputePositionConfig, offset, shift} from '@floating-ui/vue'
import {ref, watch} from 'vue'

const referenceRef = ref()
const floatingRef = ref()
const arrowRef = ref()
const isHidden = ref(true)

const props = defineProps({
    content: String,
    placement: {
        type: String,
        default: 'bottom',
    },
    errorTooltip: {
        type: Boolean,
        default: false
    },
    needsTeleporting: {
        type: Boolean,
        default: false
    },
    showTip: {
        type: Boolean,
        default: undefined
    },
})

const calculatePosition = async () => {
    const {x, y, middlewareData} = await computePosition(
        referenceRef.value,
        floatingRef.value,
        {
            placement: props.placement,
            middleware: [
                offset(4),
                shift({padding: 20}),
                arrow({element: arrowRef.value}),
            ],
        } as Partial<ComputePositionConfig>
    )


    Object.assign(floatingRef.value.style, {
        left: `${x}px`,
        top: `${y}px`,
    })

    const {x: arrowX, y: arrowY} = middlewareData.arrow ?? {}

    const opposedSideLookup: { [key: string]: string } = {
        left: 'right',
        right: 'left',
        bottom: 'top',
        top: 'bottom',
    }

    const opposedSide = opposedSideLookup[props.placement as keyof typeof opposedSideLookup] || 'bottom'

    const styleProperties: { [key: string]: string } = {
        left: arrowX ? `${arrowX}px` : '',
        top: arrowY ? `${arrowY}px` : '',
        bottom: '',
        right: '',
    }

    styleProperties[opposedSide] = '-4px'
    Object.assign(arrowRef.value.style, styleProperties)
}

const hide = () => {
    isHidden.value = true
}

const show = () => {
    isHidden.value = false
    calculatePosition()
}

watch(() => props.showTip, () => {
    if (props.showTip) {
        show()
    } else {
        hide()
    }
})

</script>

<style scoped>
@reference "@/index.css";
@reference "tailwindcss";

.floating-ref {
    @apply top-0 left-0 z-50 text-sm px-3 py-1 rounded cursor-default w-auto max-w-[80vw] whitespace-nowrap overflow-ellipsis;
}

.arrow-ref {
    @apply absolute h-[8px] w-[8px] rotate-45;
}

.error-tooltip {
    @apply text-black;
    background-color: #ffe1e1;
}

.normal-tooltip {
    @apply bg-base-100 border;
}

</style>